<template>
  <div class="query-guard">
    <!-- 头部导航 -->
    <van-nav-bar
      :title="platformName"
      fixed
      placeholder
    >
      <template #right>
        <van-icon
          name="user-circle-o"
          size="20"
          @click="goToUserCenter"
        />
      </template>
    </van-nav-bar>

    <!-- 主视觉区域 -->
    <div class="hero-section">
      <div class="hero-content">
        <h1 class="hero-title">
          {{ $t('guard.heroTitle') }}
        </h1>
        <p class="hero-subtitle">
          {{ $t('guard.heroSubtitle') }}
        </p>

        <!-- 三种查询方式卡片 -->
        <div class="query-cards">
          <div
            v-for="method in queryMethods"
            :key="method.id"
            class="query-card"
            :class="{ active: activeMethod === method.id }"
            @click="setActiveMethod(method.id)"
          >
            <div class="card-icon">
              <van-icon
                :name="method.icon"
                size="24"
              />
            </div>
            <div class="card-content">
              <h3>{{ method.title }}</h3>
              <p>{{ method.description }}</p>
            </div>
            <div class="card-arrow">
              <van-icon name="arrow" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 查询功能区 -->
    <div class="query-section">
      <div class="section-header">
        <h2>{{ activeMethodData.title }} {{ $t('guard.verification') }}</h2>
        <p>{{ activeMethodData.tips }}</p>
      </div>

      <!-- 鉴伪查询 -->
      <div
        v-if="activeMethod === 'antiFake'"
        class="method-content"
      >
        <div class="input-section">
          <van-field
            v-model="antiFakeCode"
            :placeholder="$t('guard.enterAntiFakeCode')"
            clearable
            center
          >
            <template #button>
              <van-button
                type="primary"
                size="small"
                :disabled="!antiFakeCode"
                @click="handleAntiFakeQuery"
              >
                {{ $t('guard.verifyNow') }}
              </van-button>
            </template>
          </van-field>
        </div>

        <div class="scan-section">
          <van-button
            type="default"
            block
            icon="scan"
            @click="handleScan"
          >
            {{ $t('guard.scanCodeVerify') }}
          </van-button>
        </div>

        <div class="help-tips">
          <van-collapse v-model="antiFakeTips">
            <van-collapse-item
              :title="$t('guard.howToFindCode')"
              name="1"
            >
              <ul>
                <li>{{ $t('guard.tip1') }}</li>
                <li>{{ $t('guard.tip2') }}</li>
                <li>{{ $t('guard.tip3') }}</li>
              </ul>
            </van-collapse-item>
          </van-collapse>
        </div>
      </div>

      <!-- 吊牌查询 -->
      <div
        v-if="activeMethod === 'tag'"
        class="method-content"
      >
        <div class="input-group">
          <van-field
            v-model="tagInfo.styleNumber"
            :placeholder="$t('guard.enterStyleNumber')"
            :label="$t('guard.styleNumber')"
          />
          <van-field
            v-model="tagInfo.size"
            :placeholder="$t('guard.enterSize')"
            :label="$t('guard.size')"
          />
          <van-field
            v-model="tagInfo.color"
            :placeholder="$t('guard.enterColor')"
            :label="$t('guard.color')"
          />
        </div>

        <div class="action-section">
          <van-button
            type="primary"
            block
            :disabled="!tagInfo.styleNumber"
            @click="handleTagQuery"
          >
            {{ $t('guard.queryTagInfo') }}
          </van-button>
        </div>

        <div class="example-section">
          <h4>{{ $t('guard.tagExample') }}</h4>
          <div class="example-image">
<!--            <img-->
<!--              src="@/assets/tag-example.png"-->
<!--              alt="吊牌示例"-->
<!--            >-->
            <p>{{ $t('guard.tagExampleDesc') }}</p>
          </div>
        </div>
      </div>

      <!-- 包装袋/盒查询 -->
      <div
        v-if="activeMethod === 'package'"
        class="method-content"
      >
        <div class="package-type-selector">
          <div class="type-title">
            {{ $t('guard.selectPackageType') }}
          </div>
          <van-radio-group
            v-model="packageType"
            direction="horizontal"
          >
            <van-radio name="bag">
              {{ $t('guard.packageBag') }}
            </van-radio>
            <van-radio name="box">
              {{ $t('guard.packageBox') }}
            </van-radio>
          </van-radio-group>
        </div>

        <div class="input-section">
          <van-field
            v-model="packageCode"
            :placeholder="packageType === 'bag' ? $t('guard.enterBagCode') : $t('guard.enterBoxCode')"
            clearable
            center
          >
            <template #button>
              <van-button
                type="primary"
                size="small"
                :disabled="!packageCode"
                @click="handlePackageQuery"
              >
                {{ $t('guard.verifyPackage') }}
              </van-button>
            </template>
          </van-field>
        </div>

        <div class="package-features">
          <h4>{{ $t('guard.packageFeatures') }}</h4>
          <div class="features-grid">
            <div class="feature-item">
              <van-icon name="certificate" />
              <span>{{ $t('guard.feature1') }}</span>
            </div>
            <div class="feature-item">
              <van-icon name="shield-o" />
              <span>{{ $t('guard.feature2') }}</span>
            </div>
            <div class="feature-item">
              <van-icon name="photo-o" />
              <span>{{ $t('guard.feature3') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 查询历史 -->
    <div
      v-if="queryHistory.length > 0"
      class="history-section"
    >
      <div class="section-header">
        <h3>{{ $t('guard.recentQueries') }}</h3>
        <van-button
          size="small"
          type="default"
          @click="clearHistory"
        >
          {{ $t('guard.clear') }}
        </van-button>
      </div>
      <div class="history-list">
        <div
          v-for="item in queryHistory"
          :key="item.id"
          class="history-item"
          @click="redoQuery(item)"
        >
          <div class="history-type">
            <van-tag :type="getHistoryTagType(item.type)">
              {{ getHistoryTypeText(item.type) }}
            </van-tag>
          </div>
          <div class="history-content">
            <div class="history-code">
              {{ item.code }}
            </div>
            <div class="history-time">
              {{ formatTime(item.time) }}
            </div>
          </div>
          <div class="history-result">
            <van-tag :type="item.result === 'valid' ? 'success' : 'danger'">
              {{ item.result === 'valid' ? $t('guard.genuine') : $t('guard.fake') }}
            </van-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部帮助信息 -->
    <div class="help-section">
      <van-cell-group>
        <van-cell
          :title="$t('guard.haveQuestions')"
          :value="$t('guard.viewFAQ')"
          is-link
          @click="goToFAQ"
        />
        <van-cell
          :title="$t('guard.contactCustomerService')"
          :value="$t('guard.contactUs')"
          is-link
          @click="handleContact"
        />
      </van-cell-group>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showToast, showConfirmDialog } from 'vant'

export default {
  name: 'GuardHome',
  setup () {
    const { t } = useI18n()
    const router = useRouter()

    // 平台名称
    const platformName = ref('防伪溯源')

    // 当前激活的查询方式
    const activeMethod = ref('antiFake')

    // 查询方式数据
    const queryMethods = ref([
      {
        id: 'antiFake',
        title: t('guard.antiFakeQuery'),
        description: t('guard.antiFakeDesc'),
        icon: 'shield-o',
        tips: t('guard.antiFakeTips')
      },
      {
        id: 'tag',
        title: t('guard.tagQuery'),
        description: t('guard.tagDesc'),
        icon: 'label-o',
        tips: t('guard.tagTips')
      },
      {
        id: 'package',
        title: t('guard.packageQuery'),
        description: t('guard.packageDesc'),
        icon: 'bag-o',
        tips: t('guard.packageTips')
      }
    ])

    // 当前激活的查询方式数据
    const activeMethodData = computed(() => {
      return queryMethods.value.find(method => method.id === activeMethod.value)
    })

    // 设置激活的查询方式
    const setActiveMethod = (methodId) => {
      activeMethod.value = methodId
    }

    // 鉴伪查询相关
    const antiFakeCode = ref('')
    const antiFakeTips = ref(['1'])

    // 吊牌查询相关
    const tagInfo = ref({
      styleNumber: '',
      size: '',
      color: ''
    })

    // 包装查询相关
    const packageType = ref('bag')
    const packageCode = ref('')

    // 查询历史
    const queryHistory = ref([])

    // 处理鉴伪查询
    const handleAntiFakeQuery = () => {
      if (!antiFakeCode.value) {
        showToast(t('guard.enterCodeFirst'))
        return
      }

      // 保存到历史记录
      saveToHistory({
        type: 'antiFake',
        code: antiFakeCode.value,
        result: 'valid', // 实际应该根据API返回结果
        time: new Date()
      })

      // 跳转到结果页面或显示结果
      router.push({
        path: '/query/result',
        query: {
          type: 'antiFake',
          code: antiFakeCode.value
        }
      })
    }

    // 处理吊牌查询
    const handleTagQuery = () => {
      if (!tagInfo.value.styleNumber) {
        showToast(t('guard.enterStyleNumberFirst'))
        return
      }

      // 保存到历史记录
      saveToHistory({
        type: 'tag',
        code: tagInfo.value.styleNumber,
        result: 'valid',
        time: new Date()
      })

      router.push({
        path: '/query/result',
        query: {
          type: 'tag',
          styleNumber: tagInfo.value.styleNumber,
          size: tagInfo.value.size,
          color: tagInfo.value.color
        }
      })
    }

    // 处理包装查询
    const handlePackageQuery = () => {
      if (!packageCode.value) {
        showToast(t('guard.enterPackageCodeFirst'))
        return
      }

      // 保存到历史记录
      saveToHistory({
        type: 'package',
        code: packageCode.value,
        result: 'valid',
        time: new Date()
      })

      router.push({
        path: '/query/result',
        query: {
          type: 'package',
          packageType: packageType.value,
          code: packageCode.value
        }
      })
    }

    // 扫码功能
    const handleScan = () => {
      // 调用扫码API
      showToast(t('guard.scanFunction'))
    }

    // 保存查询历史
    const saveToHistory = (query) => {
      const historyItem = {
        id: Date.now(),
        ...query
      }
      queryHistory.value.unshift(historyItem)

      // 只保留最近10条记录
      if (queryHistory.value.length > 10) {
        queryHistory.value = queryHistory.value.slice(0, 10)
      }

      // 保存到本地存储
      localStorage.setItem('queryHistory', JSON.stringify(queryHistory.value))
    }

    // 重新查询
    const redoQuery = (item) => {
      switch (item.type) {
        case 'antiFake':
          activeMethod.value = 'antiFake'
          antiFakeCode.value = item.code
          break
        case 'tag':
          activeMethod.value = 'tag'
          tagInfo.value.styleNumber = item.code
          break
        case 'package':
          activeMethod.value = 'package'
          packageCode.value = item.code
          break
      }
    }

    // 清除历史记录
    const clearHistory = () => {
      showConfirmDialog({
        title: t('guard.confirmClear'),
        message: t('guard.clearHistoryConfirm')
      }).then(() => {
        queryHistory.value = []
        localStorage.removeItem('queryHistory')
        showToast(t('guard.clearSuccess'))
      })
    }

    // 获取历史记录标签类型
    const getHistoryTagType = (type) => {
      const types = {
        antiFake: 'primary',
        tag: 'success',
        package: 'warning'
      }
      return types[type] || 'default'
    }

    // 获取历史记录类型文本
    const getHistoryTypeText = (type) => {
      const texts = {
        antiFake: t('guard.antiFakeQuery'),
        tag: t('guard.tagQuery'),
        package: t('guard.packageQuery')
      }
      return texts[type] || type
    }

    // 格式化时间
    const formatTime = (time) => {
      return new Date(time).toLocaleString()
    }

    // 导航功能
    const goToUserCenter = () => {
      router.push('/user')
    }

    const goToFAQ = () => {
      router.push('/faq')
    }

    const handleContact = () => {
      router.push('/contact')
    }

    // 初始化加载历史记录
    onMounted(() => {
      const savedHistory = localStorage.getItem('queryHistory')
      if (savedHistory) {
        queryHistory.value = JSON.parse(savedHistory)
      }
    })

    return {
      platformName,
      activeMethod,
      queryMethods,
      activeMethodData,
      antiFakeCode,
      antiFakeTips,
      tagInfo,
      packageType,
      packageCode,
      queryHistory,
      setActiveMethod,
      handleAntiFakeQuery,
      handleTagQuery,
      handlePackageQuery,
      handleScan,
      redoQuery,
      clearHistory,
      getHistoryTagType,
      getHistoryTypeText,
      formatTime,
      goToUserCenter,
      goToFAQ,
      handleContact
    }
  }
}
</script>

<style lang="scss" scoped>
.query-guard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.hero-section {
  padding: 20px 16px;
  background: white;
  border-radius: 0 0 20px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  .hero-content {
    max-width: 1200px;
    margin: 0 auto;
  }

  .hero-title {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    text-align: center;
    margin-bottom: 8px;
  }

  .hero-subtitle {
    font-size: 14px;
    color: #64748b;
    text-align: center;
    margin-bottom: 24px;
  }
}

.query-cards {
  display: grid;
  gap: 12px;
}

.query-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #3b82f6;
    background: #f0f7ff;
  }

  &.active {
    border-color: #3b82f6;
    background: #dbeafe;
  }

  .card-icon {
    margin-right: 12px;
    color: #3b82f6;
  }

  .card-content {
    flex: 1;

    h3 {
      margin: 0 0 4px;
      font-size: 16px;
      font-weight: 600;
      color: #1e293b;
    }

    p {
      margin: 0;
      font-size: 12px;
      color: #64748b;
    }
  }

  .card-arrow {
    color: #94a3b8;
  }
}

.query-section {
  padding: 24px 16px;
  max-width: 1200px;
  margin: 0 auto;

  .section-header {
    text-align: center;
    margin-bottom: 24px;

    h2 {
      margin: 0 0 8px;
      font-size: 20px;
      font-weight: 600;
      color: #1e293b;
    }

    p {
      margin: 0;
      color: #64748b;
      font-size: 14px;
    }
  }
}

.method-content {
  background: white;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.input-group {
  .van-field {
    margin-bottom: 12px;
  }
}

.scan-section,
.action-section {
  margin-top: 16px;
}

.help-tips {
  margin-top: 20px;
}

.package-type-selector {
  margin-bottom: 20px;

  .type-title {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 12px;
  }

  .van-radio-group {
    display: flex;
    gap: 20px;
  }
}

.example-section {
  margin-top: 24px;

  h4 {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  .example-image {
    text-align: center;

    img {
      max-width: 200px;
      border-radius: 8px;
      margin-bottom: 8px;
    }

    p {
      margin: 0;
      font-size: 12px;
      color: #64748b;
    }
  }
}

.package-features {
  margin-top: 24px;

  h4 {
    margin: 0 0 16px;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }

  .features-grid {
    display: grid;
    gap: 12px;
  }

  .feature-item {
    display: flex;
    align-items: center;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;

    .van-icon {
      margin-right: 8px;
      color: #3b82f6;
    }

    span {
      font-size: 14px;
      color: #475569;
    }
  }
}

.history-section {
  padding: 0 16px 24px;
  max-width: 1200px;
  margin: 0 auto;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #1e293b;
    }
  }
}

.history-list {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.history-item {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }

  .history-type {
    margin-right: 12px;
  }

  .history-content {
    flex: 1;

    .history-code {
      font-size: 14px;
      font-weight: 500;
      color: #1e293b;
      margin-bottom: 4px;
    }

    .history-time {
      font-size: 12px;
      color: #64748b;
    }
  }
}

.help-section {
  padding: 0 16px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

// PC端优化
@media (min-width: 768px) {
  .hero-section {
    padding: 40px 32px;

    .hero-title {
      font-size: 32px;
    }

    .hero-subtitle {
      font-size: 16px;
    }
  }

  .query-cards {
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .query-card {
    padding: 24px;
    flex-direction: column;
    text-align: center;

    .card-icon {
      margin-right: 0;
      margin-bottom: 12px;
    }

    .card-arrow {
      display: none;
    }
  }

  .query-section {
    padding: 40px 32px;
  }

  .method-content {
    padding: 32px;
  }

  .input-group {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .features-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .history-section {
    padding: 0 32px 40px;
  }

  .help-section {
    padding: 0 32px 40px;
  }
}

// 大屏设备优化
@media (min-width: 1200px) {
  .query-guard {
    background: white;
  }

  .hero-section {
    max-width: 1200px;
    margin: 0 auto;
    border-radius: 20px;
    margin-top: 20px;
  }

  .query-section,
  .history-section,
  .help-section {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
